import { Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

export function DeleteConfirmDialog({
  title,
  titleId,
  description,
  descriptionId,
  busy,
  onCancel,
  onConfirm,
  confirmLabel = "Delete",
  confirmDisabled = false,
  backdropLabel = "Cancel delete",
  children,
}: {
  title: ReactNode;
  titleId: string;
  description: ReactNode;
  descriptionId: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  backdropLabel?: string;
  children?: ReactNode;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const busyRef = useRef(busy);
  const cancelRef = useRef(onCancel);
  busyRef.current = busy;
  cancelRef.current = onCancel;

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelButton.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!busyRef.current) cancelRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const focusable = [...dialog.current.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex='-1'])")];
      if (!focusable.length) {
        event.preventDefault();
        dialog.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.removeEventListener("keydown", keydown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, []);

  return <div className="dialog-layer">
    <button className="dialog-backdrop" type="button" aria-label={backdropLabel} onClick={onCancel} disabled={busy} />
    <div ref={dialog} className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} tabIndex={-1}>
      <div className="dialog-title-row"><Trash2 className="dialog-title-icon" size={22} aria-hidden="true" /><h2 id={titleId}>{title}</h2></div>
      <p id={descriptionId}>{description}</p>
      {children}
      <div className="dialog-actions">
        <button ref={cancelButton} className="secondary-button" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="danger-button" type="button" onClick={onConfirm} disabled={busy || confirmDisabled}>{busy ? "Deleting…" : confirmLabel}</button>
      </div>
    </div>
  </div>;
}
